import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../../lib/utils";
import { Dash } from "./dash";
import { textD2CSizes } from "./text-d2c.constants";

type TextD2CTagName = "p" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

export type TextD2CProps = Omit<
  HTMLAttributes<
    HTMLParagraphElement | HTMLLIElement | HTMLHeadingElement | HTMLQuoteElement
  >,
  "className"
> & {
  children?: ReactNode;
  variant?: VariantProps<typeof textD2CVariants>["variant"];
  color?:
    | "black"
    | "white"
    | "alabastergrey"
    | "blazeorange"
    | "neonchartreuse";
  /**
   * Arbitrary CSS color (e.g. a hex like `#FF6600`) applied inline. When set it
   * overrides the `color` palette variant and is also used for the dash. Lets
   * editors pick a headline color outside the fixed design-system palette.
   */
  colorHex?: string;
  /** Style variants for text (e.g. 'line-through') */
  style?: VariantProps<typeof textD2CVariants>["style"];
  dash?: VariantProps<typeof textD2CVariants>["dash"];
  tagName?: TextD2CTagName;
};

// Figma drop shadow: x 0, y 0, blur 24, color #075056 at 15%.
const heroHeadlineShadow = "[text-shadow:0_0_24px_rgb(7_80_86/15%)]";

const textD2CVariants = cva("", {
  variants: {
    variant: {
      h1: `wrap-break-word mb-8 block hyphens-auto break-normal font-bold font-brand last:mb-0 only:mb-0 ${textD2CSizes.h1}`,
      h2: `wrap-break-word mb-8 block hyphens-auto break-normal font-bold font-brand only:mb-0 ${textD2CSizes.h2}`,
      h3: `mb-8 block font-bold font-brand last:mb-0 only:mb-0 ${textD2CSizes.h3}`,
      h4: `mb-6 block font-bold font-brand last:mb-0 only:mb-0 ${textD2CSizes.h4}`,
      h5: `mb-6 block font-bold font-brand last:mb-0 only:mb-0 ${textD2CSizes.h5}`,
      h6: `mb-4 block font-bold font-brand last:mb-0 only:mb-0 ${textD2CSizes.h6}`,
      "p-xlarge": `mb-[1em] font-brand font-normal last:mb-0 only:mb-0 ${textD2CSizes["p-xlarge"]}`,
      "p-large": `mb-[1em] font-brand font-normal last:mb-0 only:mb-0 ${textD2CSizes["p-large"]}`,
      p: `mb-[1em] font-brand font-normal last:mb-0 only:mb-0 ${textD2CSizes.p}`,
      "p-small": `mb-[1em] font-brand font-normal last:mb-0 only:mb-0 ${textD2CSizes["p-small"]}`,
      "p-xsmall": `mb-[1em] font-brand font-normal last:mb-0 only:mb-0 ${textD2CSizes["p-xsmall"]}`,
      pillar: `mb-[1em] font-brand font-bold uppercase last:mb-0 only:mb-0 ${textD2CSizes.pillar}`,
    },
    color: {
      black: "text-black",
      white: "text-white",
      alabastergrey: "text-alabastergrey",
      blazeorange: "text-blazeorange",
      neonchartreuse: "text-neonchartreuse",
    },
    style: {
      "line-through": "line-through",
      uppercase: "uppercase",
      "hero-section-small": `text-[36px] leading-[36px] tracking-[0%] md:text-[48px] md:leading-[48px] xl:text-[64px] xl:leading-[64px] ${heroHeadlineShadow}`,
      "hero-section": `text-[44px] leading-[44px] tracking-[0%] md:text-[68px] md:leading-[68px] xl:text-[100px] xl:leading-[100px] ${heroHeadlineShadow}`,
      "hero-section-large": `text-[54px] leading-[54px] tracking-[0%] md:text-[96px] md:leading-[96px] xl:text-[142px] xl:leading-[142px] ${heroHeadlineShadow}`,
      "hero-section-subline": `text-[28px] leading-[30px] md:text-[30px] md:leading-[32px] ${heroHeadlineShadow}`,
      regular: "font-normal",
      bold: "font-bold",
      "no-mb": "!mb-0",
      "teaser-label":
        "!mb-0 text-[18px] leading-[24px] md:text-[30px] md:leading-[34px]",
    },
    dash: {
      true: "",
      false: "",
    },
  },
  defaultVariants: {
    variant: "p",
    color: "black",
  },
});

// eslint-disable-next-line react/destructuring-assignment
export function TextD2C({
  children,
  variant = "p",
  color = "black",
  colorHex,
  style: styleVariant,
  dash: dashVariant = false,
  tagName,
  ...props
}: Readonly<TextD2CProps>) {
  const Tag = tagName ?? getTag(variant);
  const variantClasses = textD2CVariants({
    variant,
    color,
    style: styleVariant,
    dash: dashVariant,
  });
  // An explicit hex wins over the palette class via inline style.
  const inlineStyle = colorHex ? { color: colorHex } : undefined;

  return (
    <Tag
      className={cn(variantClasses, "component-text-d2c")}
      data-testid="text-d2c"
      style={inlineStyle}
      {...(Tag === "h1" ? { tabIndex: -1, ...props } : props)}
    >
      {children}

      {isHeadline(variant) && dashVariant && (
        <Dash
          color={color}
          colorHex={colorHex}
          variant={variant as "h1" | "h2" | "h3" | "h4" | "h5" | "h6"}
        />
      )}
    </Tag>
  );
}

function getTag(
  variant: TextD2CProps["variant"]
): "p" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6" {
  switch (variant) {
    case "h1":
      return "h1";
    case "h2":
      return "h2";
    case "h3":
      return "h3";
    case "h4":
      return "h4";
    case "h5":
      return "h5";
    case "h6":
      return "h6";
    case "p-xlarge":
    case "p-large":
    case "p":
    case "p-small":
    case "p-xsmall":
    case "pillar":
      return "p";
    default:
      return "p";
  }
}

function isHeadline(variant: TextD2CProps["variant"]): boolean {
  return (
    variant === "h1" ||
    variant === "h2" ||
    variant === "h3" ||
    variant === "h4" ||
    variant === "h5" ||
    variant === "h6"
  );
}
